// src/lib/consent.ts
// Analytics cookie consent (GDPR / ePrivacy). Google Analytics is only loaded after
// the visitor accepts; the loader itself lives in index.html (window.hnhLoadAnalytics).

export type ConsentChoice = 'granted' | 'denied';

const CONSENT_STORAGE_KEY = 'hnh-cookie-consent';
export const OPEN_CONSENT_SETTINGS_EVENT = 'hnh:open-cookie-settings';

type AnalyticsWindow = typeof window & {
  hnhLoadAnalytics?: () => void;
  hnhAnalyticsLoaded?: boolean;
  gtag?: (...args: unknown[]) => void;
};

export function getConsent(): ConsentChoice | null {
  try {
    const value = localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
}

export function setConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch {
    // Storage unavailable: the choice only applies to this page view
  }

  const w = window as AnalyticsWindow;

  if (choice === 'granted') {
    w.hnhLoadAnalytics?.();
    return;
  }

  // Consent withdrawn: stop analytics and remove the cookies it already set
  w.gtag?.('consent', 'update', { analytics_storage: 'denied' });
  deleteAnalyticsCookies();
  if (w.hnhAnalyticsLoaded) {
    // gtag.js cannot be unloaded from a running page; reload so it is gone
    window.location.reload();
  }
}

export function openConsentSettings(): void {
  window.dispatchEvent(new Event(OPEN_CONSENT_SETTINGS_EVENT));
}

function deleteAnalyticsCookies(): void {
  const host = window.location.hostname;
  const domains = ['', host, `.${host}`, `.${host.replace(/^www\./, '')}`];

  document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0].trim())
    .filter((name) => name === '_ga' || name.startsWith('_ga_') || name === '_gid')
    .forEach((name) => {
      domains.forEach((domain) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${
          domain ? `; domain=${domain}` : ''
        }`;
      });
    });
}
